import { IPublicSession, updatePublicSession, userSession } from 'state/session';
import { CancelledError } from 'utils/functions';
import { IntegrationType, integrationType } from 'utils/ui/app';
import logger from 'utils/log';
import { clearDirty, getDirtyKeys } from './dirtyKeys';
import { emptyLiveStreamIdentifiersToOmit } from './liveStreamClobberGuard';
import { repairPublicSession } from './readRepair';

/**
 * Re-send driver for unconfirmed session edits (AVS-4399).
 *
 * `dirtyKeys` has always documented that the sync layer "keeps keys dirty when a PUT fails so they
 * can be re-sent instead of being silently lost" — but nothing ever re-sent them. A key that missed
 * its PUT stayed dirty for the life of the session, and because both the remote-apply chokepoint
 * and the reconnect read-repair skip dirty keys, the client could neither push its edit nor accept
 * the server's. The session was stuck in both directions until it was reset.
 *
 * This module owns that retry policy in one place: backoff, attempt cap, the conditions under which
 * a retry is pointless, and what to do when the cap is reached. Everything else just calls
 * {@link scheduleDirtyFlush} or {@link cancelDirtyFlush}. `dirtyKeys` stays a pure Set store.
 */

/** Backoff schedule. Its length IS the attempt cap. */
const RETRY_DELAYS_MS = [2_000, 5_000, 12_000];

let timeoutId: ReturnType<typeof setTimeout> | undefined;
let attempt = 0;
/**
 * The keys this retry chain is responsible for, snapshotted when it starts.
 *
 * Surrender abandons a local edit, so it must only ever abandon the edit it was retrying. A chain
 * can run for ~20 s, and the dealer keeps working during it: keys dirtied AFTER it began belong to
 * a write that has not had its own chance yet, and clearing them would strip their protection at
 * the remote-apply chokepoint moments after the user made the edit.
 */
let chainKeys: Set<keyof IPublicSession> | undefined;

/**
 * Conditions re-checked at FIRE time, never captured at schedule time.
 *
 * A timer that outlives its session is how AVS-4331 got a torn-down session resurrected by a stale
 * payload, so a retry must re-derive whether it still has anything to do — and whether the session
 * it was scheduled for still exists — at the moment it runs.
 */
function canFlush(): boolean {
  const session = userSession();
  return getDirtyKeys().size > 0 && !!session.get('id') && !session.get('endedAt') && !session.get('offline');
}

function reset(): void {
  if (timeoutId !== undefined) {
    clearTimeout(timeoutId);
  }
  timeoutId = undefined;
  attempt = 0;
  chainKeys = undefined;
}

/**
 * Cap reached: stop pushing and take the server's record instead.
 *
 * Leaving the keys dirty forever would reproduce the very latch this driver exists to break — the
 * edit is never re-sent AND `applyRemote`/`readRepair` keep skipping those keys, so the client can
 * never re-converge. Dropping the local edit is the lesser loss, and it must happen BEFORE the
 * repair GET or the repair would skip the same keys.
 */
function surrender(): void {
  // Only the keys this chain owns AND that are still dirty. A key dirtied after the chain started
  // keeps its protection and its own future chain; a key already confirmed needs nothing.
  const dirty = getDirtyKeys();
  const abandoned = [...(chainKeys ?? dirty)].filter((key) => dirty.has(key));
  reset();
  if (abandoned.length === 0) {
    rearmForRemainingKeys();
    return;
  }
  logger.error('[Session] Unconfirmed edits could not be flushed; taking the server record instead', {
    abandoned,
  });
  clearDirty(abandoned);
  void repairPublicSession();
  rearmForRemainingKeys();
}

/**
 * Give the keys dirtied mid-chain the chain they were promised.
 *
 * `schedule()` deliberately ignores a call while a timer is armed, so every `scheduleDirtyFlush()`
 * a mid-chain edit made was swallowed by the chain that has just ended. Surrender clears only the
 * keys it owns, and the repair GET then skips the ones still dirty — so ending here without
 * re-arming leaves them dirty with nothing able to re-send them. That is precisely the latch this
 * module exists to break, moved one edit later.
 */
function rearmForRemainingKeys(): void {
  if (getDirtyKeys().size > 0) {
    schedule();
  }
}

async function fire(): Promise<void> {
  timeoutId = undefined;

  if (!canFlush()) {
    reset();
    return;
  }

  // A window is already open: it will carry the dirty keys itself, and re-arming would both reset
  // its 1 s timer and, under latest-wins, replace args it is holding such as the clobber guard's
  // omitKeys. Wait for it instead.
  //
  // This covers the waiting window only. `debounce` clears its pending ref before invoking, so a
  // PUT that has already committed reads as not pending and a retry can be issued alongside it.
  // That is survivable rather than ideal: both bodies are rebuilt from the same live state, and CAS
  // rejects the loser once a version is known.
  if (updatePublicSession.isPending()) {
    schedule();
    return;
  }

  attempt += 1;
  try {
    // The body is rebuilt from LIVE state at commit time rather than from a snapshot taken when the
    // edit failed, so a retry can never carry values the session has since moved past. What it must
    // NOT do is send a body shaped differently from the write it is replacing:
    //
    //  - `updateEverything` is recomputed exactly as `syncToServer` computes it. It is not
    //    cosmetic. The server strips `prString` from the broadcast without it
    //    (`SessionService.updateAndEmit`), so a retried car change would land in Dynamo and never
    //    reach the customer — this ticket's own symptom, reintroduced by the fix for it.
    //  - `forceFullBody` keeps the retry off the delta path. A delta is right for the write that
    //    first carried an edit; for a re-send after an unconfirmed write we no longer know which
    //    keys the server actually took, so send the whole record.
    //  - `omitKeys` re-applies the clobber guard. The driver reaches `updatePublicSession`
    //    directly, so without this an `aveSessionId` that was dirtied earlier and has since been
    //    emptied would go out and tear down the live stream.
    const updateEverything =
      integrationType() === IntegrationType.WebComponent &&
      (userSession().get('activeExperience') === '2D' || userSession().isLocalAveSession);
    const omitKeys = emptyLiveStreamIdentifiersToOmit(getDirtyKeys());
    await updatePublicSession({
      updateEverything,
      forceFullBody: true,
      ...(omitKeys.length > 0 ? { omitKeys } : {}),
    });
  } catch (error) {
    if (error instanceof CancelledError) {
      // A cancel means the session was reset or ended while we were queued. That is terminal,
      // not a failure to retry.
      reset();
      return;
    }
    logger.warn('[Session] Flush attempt for unconfirmed edits failed', error);
  }

  if (getDirtyKeys().size === 0) {
    reset();
    return;
  }
  schedule();
}

/**
 * Ask for the dirty set to be re-sent, if it still needs sending when the timer fires.
 *
 * Safe to call repeatedly — an already-armed timer is left alone, so a burst of failing writes
 * produces one retry chain rather than one per write.
 */
export function scheduleDirtyFlush(): void {
  schedule();
}

function schedule(): void {
  if (timeoutId !== undefined) {
    return;
  }
  if (attempt >= RETRY_DELAYS_MS.length) {
    surrender();
    return;
  }
  if (chainKeys === undefined) {
    chainKeys = new Set(getDirtyKeys());
  }
  timeoutId = setTimeout(() => void fire(), RETRY_DELAYS_MS[attempt]);
}

/**
 * Drop any pending retry. Called from session tear-down, where a surviving timer could write to —
 * or resurrect — a session that no longer exists.
 */
export function cancelDirtyFlush(): void {
  reset();
}
